import { StatusBarType } from '@buildwise/libs/ui';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { ConfigEntryType } from 'config/config';
import {
  createContext,
  useReducer,
  useContext,
  ReactNode,
  Dispatch,
  useCallback,
  useEffect,
} from 'react';

type State = {
  articleIds: string[];
  isFullScreen: boolean;
  isOpenedLessonForm: boolean;
  selectedArticle: ArticleType | undefined;
  articleFields: ConfigEntryType[];
  selectedLessonType: StatusBarType | undefined;
  wrapText: boolean;
  spanRows: boolean;
  view: 'TREE' | 'TABLE';
  showSelectMilestone: boolean;
};

export enum ProjectRetroContextActionTypes {
  UPDATE_STATE = 'UPDATE_STATE',
}

type Action = {
  type?: ProjectRetroContextActionTypes.UPDATE_STATE;
  payload: Partial<State>;
};

const initialState: State = {
  articleIds: [],
  isFullScreen: false,
  isOpenedLessonForm: false,
  selectedArticle: undefined,
  articleFields: [],
  selectedLessonType: undefined,
  wrapText: false,
  spanRows: false,
  view: 'TABLE',
  showSelectMilestone: false,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ProjectRetroContextActionTypes.UPDATE_STATE:
      return {
        ...state,
        ...action.payload,
      };

    default:
      return { ...state, ...action.payload };
  }
};

export const ProjectRetroContext = createContext<{
  state: State;
  dispatch: Dispatch<((options: { state: State }) => Action) | Action>;
}>({
  state: initialState,
  dispatch: () => {},
});

const ProjectRetroProvider: React.FC<{ children: ReactNode; initialState?: Partial<State> }> = ({
  children,
  initialState: _initialState,
}) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState, ..._initialState });
  const handleDispatch = useCallback(
    (action: ((options: { state: State }) => Action) | Action) => {
      dispatch(typeof action === 'function' ? action({ state }) : action);
    },
    [state],
  );

  useEffect(() => {
    if (_initialState) {
      dispatch({
        payload: _initialState,
      });
    }
  }, [_initialState]);

  return (
    <ProjectRetroContext.Provider value={{ state, dispatch: handleDispatch }}>
      {children}
    </ProjectRetroContext.Provider>
  );
};

export const useProjectRetroContext = () => {
  const context = useContext(ProjectRetroContext);

  return context;
};

export default ProjectRetroProvider;
